import { useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import { useReplayStore } from "@/stores/replayStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { useUIStore } from "@/stores/uiStore";

function playReplayCompleteTone() {
  if (typeof window === "undefined") {
    return;
  }

  const AudioContextCtor =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;

  if (!AudioContextCtor) {
    return;
  }

  try {
    const audioContext = new AudioContextCtor();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(
      660,
      audioContext.currentTime + 0.18,
    );
    gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.04,
      audioContext.currentTime + 0.02,
    );
    gainNode.gain.exponentialRampToValueAtTime(
      0.0001,
      audioContext.currentTime + 0.22,
    );

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.24);
    oscillator.onended = () => {
      void audioContext.close().catch(() => null);
    };
  } catch {
    // Ignore autoplay or device audio failures.
  }
}


/**
 * RAF-based replay timer for smooth, jank-free playback.
 *
 * The Web Worker is mounted ONCE on hook mount and reused across all
 * play/pause/speed changes:
 * - Play:   postMessage({ type: "START", speedMultiplier })
 * - Pause:  postMessage({ type: "STOP" })
 * - Speed change: postMessage({ type: "RESTART", speedMultiplier })
 *   → replaces the interval without respawning the worker, so the RAF
 *     fallback accumulator and lastFrameTime are never reset mid-playback.
 */
export function useReplayTimer() {
  const { isActive, sessionState, speed } = useReplayStore(
    useShallow((state) => ({
      isActive: state.isActive,
      sessionState: state.sessionState,
      speed: state.speed,
    })),
  );
  const soundAlertsEnabled = useSettingsStore(
    (state) => state.soundAlertsEnabled,
  );
  const workerRef = useRef<Worker | null>(null);
  const rafActiveRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const prevSessionStateRef = useRef(sessionState);

  // ── Mount the worker once ────────────────────────────────────────────────
  useEffect(() => {
    // RAF fallback state — stable for the entire hook lifetime
    let accumulator = 0;
    let lastFrameTime = performance.now();

    function startRafFallback() {
      if (rafActiveRef.current) return;
      rafActiveRef.current = true;
      lastFrameTime = performance.now();

      const tick = (now: number) => {
        const delta = now - lastFrameTime;
        lastFrameTime = now;
        accumulator += Math.min(delta, 2000);

        // Read speed from store on each frame — always current
        const currentSpeed = useReplayStore.getState().speed;
        const msPerStep = 1000 / currentSpeed;
        const maxStepsPerFrame = 1;

        let stepsThisFrame = 0;

        while (accumulator >= msPerStep && stepsThisFrame < maxStepsPerFrame) {
          const state = useReplayStore.getState();
          if (state.sessionState !== "playing") return;

          state.stepForward();

          const nextState = useReplayStore.getState();
          if (nextState.position >= nextState.totalCandles - 1) {
            nextState.transitionTo("ended");
            return;
          }

          accumulator -= msPerStep;
          stepsThisFrame++;
        }

        if (accumulator > msPerStep * 3) accumulator = msPerStep * 3;

        rafIdRef.current = requestAnimationFrame(tick);
      };

      rafIdRef.current = requestAnimationFrame(tick);
    }

    try {
      const worker = new Worker(
        new URL("../workers/replayTicker.ts", import.meta.url),
        { type: "module" },
      );
      workerRef.current = worker;

      worker.onmessage = (event: MessageEvent<{ type: string }>) => {
        if (event.data.type !== "TICK") return;

        const state = useReplayStore.getState();
        if (state.sessionState !== "playing") {
          workerRef.current?.postMessage({ type: "STOP" });
          return;
        }

        state.stepForward();

        const next = useReplayStore.getState();
        if (next.position >= next.totalCandles - 1) {
          next.transitionTo("ended");
          workerRef.current?.postMessage({ type: "STOP" });
          return;
        }

      };

      worker.onerror = () => {
        // Worker failed — fall back to RAF (uses current speed from store)
        workerRef.current?.terminate();
        workerRef.current = null;
        startRafFallback();
      };
    } catch {
      // Worker constructor failed (SSR / unsupported) — use RAF fallback
      startRafFallback();
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ type: "STOP" });
        workerRef.current.terminate();
        workerRef.current = null;
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      rafActiveRef.current = false;
    };
  }, []); // mount once — never recreate

  // ── Play / pause control ─────────────────────────────────────────────────
  useEffect(() => {
    const worker = workerRef.current;
    if (!isActive || sessionState !== "playing") {
      worker?.postMessage({ type: "STOP" });
      return;
    }
    worker?.postMessage({ type: "START", speedMultiplier: speed });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, sessionState]); // 'speed' intentionally omitted — handled by the effect below

  // ── Speed change — RESTART sends new interval without tearing down worker ─
  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    const { sessionState: currentState } = useReplayStore.getState();
    if (currentState === "playing") {
      worker.postMessage({ type: "RESTART", speedMultiplier: speed });
    }
  }, [speed]);

  // ── Replay complete tone + banner/modal ─────────────────────────────────────
  useEffect(() => {
    const wasPlaying = prevSessionStateRef.current === "playing";
    prevSessionStateRef.current = sessionState;

    if (sessionState !== "ended") {
      return;
    }

    // Only trigger feedback on natural completion (playing → ended)
    if (!wasPlaying) return;

    if (soundAlertsEnabled) {
      playReplayCompleteTone();
    }

    if (window.innerWidth < 768) {
      useUIStore.getState().openModal("replayEnd");
      return;
    }

    useReplayStore.getState().showEndBanner();
  }, [sessionState, soundAlertsEnabled]);
}
